import classNames from 'classnames'
import React from 'react'

export interface BubbleTailProps {
  /**
   * Raw extracted accent colour, published as `--accent` so the tail derives
   * the same surface as the card in CSS (`styles.css`), or `undefined` to fall
   * back to this side's plain bubble fill. The tail paints outside the shell's
   * box and can't inherit its background, so it runs the derivation off its own
   * `--accent`.
   */
  accentHex?: string
  /** Sender tails hang off the right edge, receiver tails mirror to the left. */
  side: 'sender' | 'receiver'
  /**
   * `false` renders `children` untouched — a card mid-run has no tail because
   * a later bubble in the run owns it.
   */
  visible: boolean
  children: React.ReactNode
}

/**
 * Wraps a `LinkAttachment` card in the chat bubble tail from the messaging
 * design system, painted to match the card's own bottom edge.
 *
 * The geometry and mask live in `styles.css` (`.messaging-link-card-tail`),
 * mirroring the text-bubble tail (`MessageTail`) they share a silhouette with.
 * A card carries its own accent-tinted tail rather than the surface-tinted
 * `MessageTail`, so `StreamAttachmentMessage` renders `MessageTail` only on the
 * trailing caption bubble, never on the card bubble itself.
 */
const BubbleTail: React.FC<BubbleTailProps> = ({
  accentHex,
  side,
  visible,
  children,
}) => {
  if (!visible) return <>{children}</>

  // Sender tails sit on the dark stop, receiver tails on the pale one — the
  // same mapping the cards use (`SentCard` is `dark`, `ReceivedCard` `light`).
  const variant = side === 'sender' ? 'dark' : 'light'
  const accentStyle = accentHex
    ? ({ '--accent': accentHex } as React.CSSProperties)
    : undefined

  return (
    // `isolate` keeps the tail's negative z-index from escaping behind the
    // thread background; `w-fit` stops the wrapper from stretching in the
    // composer's flex list.
    <div className="relative isolate w-fit">
      {children}
      <span
        aria-hidden
        data-testid="link-card-tail"
        data-variant={variant}
        style={accentStyle}
        className={classNames(
          'messaging-link-card-tail',
          side === 'receiver' && 'messaging-link-card-tail--received'
        )}
      />
    </div>
  )
}

export default BubbleTail
